import { useState, useEffect } from "react";

const CATEGORIES = [
  { name: "อาหาร", icon: "🍜", color: "#FF6B6B" },
  { name: "ช้อปปิ้ง", icon: "🛍️", color: "#FF9F43" },
  { name: "เดินทาง", icon: "✈️", color: "#4ECDC4" },
  { name: "บันเทิง", icon: "🎬", color: "#A29BFE" },
  { name: "สุขภาพ", icon: "💊", color: "#55EFC4" },
  { name: "อื่นๆ", icon: "📦", color: "#FD79A8" },
];

// โหลดข้อมูลจาก localStorage (ถ้ามี) หรือเริ่มด้วยข้อมูลว่าง
const loadData = (key, fallback) => {
  try {
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : fallback;
  } catch { return fallback; }
};

const INIT_CARDS = loadData("ct_cards", []);
const INIT_TX = loadData("ct_txs", []);
const INIT_PAYMENTS = loadData("ct_payments", []);

export default function App() {
  const [tab, setTab] = useState("home");
  const [txs, setTxs] = useState(INIT_TX);
  const [cards, setCards] = useState(INIT_CARDS);
  const [payments, setPayments] = useState(INIT_PAYMENTS);
  const [activeCard, setActiveCard] = useState(0);

  // Modals
  const [showAddTx, setShowAddTx] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showEditTx, setShowEditTx] = useState(null); // tx object
  const [showEditPayment, setShowEditPayment] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null); // {type, id}
  const [showAddCard, setShowAddCard] = useState(false);
  const [showEditCard, setShowEditCard] = useState(null);

  const CARD_COLORS = [
    ["#FF6B6B","#FF8E53"],["#A29BFE","#6C5CE7"],["#00CEC9","#00B894"],
    ["#FD79A8","#e84393"],["#FDCB6E","#e17055"],["#74B9FF","#0984e3"],
  ];
  const blankCard = { name:"", last4:"", limit:"", colorIdx:0 };
  const [cardForm, setCardForm] = useState(blankCard);

  // Forms
  const blankTx = (cardList) => ({ desc: "", amount: "", cat: CATEGORIES[0].name, cardId: cardList[0]?.id || "" });
  const [txForm, setTxForm] = useState({ desc: "", amount: "", cat: CATEGORIES[0].name, cardId: "" });
  const [payForm, setPayForm] = useState({ cardId: "", amount: "", date: "วันนี้", note: "" });
  const [shake, setShake] = useState(false);

  const triggerShake = () => { setShake(true); setTimeout(() => setShake(false), 400); };

  // บันทึกลง localStorage ทุกครั้งที่ข้อมูลเปลี่ยน
  useEffect(() => { localStorage.setItem("ct_cards", JSON.stringify(cards)); }, [cards]);
  useEffect(() => { localStorage.setItem("ct_txs", JSON.stringify(txs)); }, [txs]);
  useEffect(() => { localStorage.setItem("ct_payments", JSON.stringify(payments)); }, [payments]);

  // sync payForm.cardId เมื่อมีบัตรครั้งแรก
  useEffect(() => {
    if (cards.length > 0 && !payForm.cardId) setPayForm(f => ({...f, cardId: cards[0].id}));
    if (cards.length > 0 && !txForm.cardId) setTxForm(f => ({...f, cardId: cards[0].id}));
  }, [cards]);

  // Derived
  const getCardSpend = (cardId) => txs.filter(t => t.cardId === cardId).reduce((s, t) => s + t.amount, 0);
  const getCardPaid = (cardId) => payments.filter(p => p.cardId === cardId).reduce((s, p) => s + p.amount, 0);
  const getCardBalance = (cardId) => getCardSpend(cardId) - getCardPaid(cardId);
  const getCard = (cardId) => cards.find(c => c.id === cardId);

  // Add Transaction
  const addTx = () => {
    if (!txForm.desc || !txForm.amount) { triggerShake(); return; }
    const cat = CATEGORIES.find(c => c.name === txForm.cat);
    setTxs([{ id: Date.now(), desc: txForm.desc, amount: Number(txForm.amount), cat: txForm.cat, cardId: txForm.cardId, date: "วันนี้", icon: cat.icon, color: cat.color }, ...txs]);
    setTxForm(blankTx(cards));
    setShowAddTx(false);
  };

  // Edit Transaction
  const saveEditTx = () => {
    if (!showEditTx.desc || !showEditTx.amount) { triggerShake(); return; }
    const cat = CATEGORIES.find(c => c.name === showEditTx.cat);
    setTxs(txs.map(t => t.id === showEditTx.id ? { ...showEditTx, amount: Number(showEditTx.amount), icon: cat.icon, color: cat.color } : t));
    setShowEditTx(null);
  };

  // Delete Transaction
  const deleteTx = (id) => { setTxs(txs.filter(t => t.id !== id)); setShowDeleteConfirm(null); setShowEditTx(null); };

  // Add Payment
  const addPayment = () => {
    if (!payForm.amount) { triggerShake(); return; }
    setPayments([{ id: Date.now(), ...payForm, amount: Number(payForm.amount) }, ...payments]);
    setPayForm({ cardId: cards[0]?.id || "", amount: "", date: "วันนี้", note: "" });
    setShowPayment(false);
  };

  // Edit Payment
  const saveEditPayment = () => {
    if (!showEditPayment.amount) { triggerShake(); return; }
    setPayments(payments.map(p => p.id === showEditPayment.id ? { ...showEditPayment, amount: Number(showEditPayment.amount) } : p));
    setShowEditPayment(null);
  };

  // Delete Payment
  const deletePayment = (id) => { setPayments(payments.filter(p => p.id !== id)); setShowDeleteConfirm(null); setShowEditPayment(null); };

  // Add Card
  const addCard = () => {
    if (!cardForm.name || !cardForm.last4 || !cardForm.limit) { triggerShake(); return; }
    const col = CARD_COLORS[cardForm.colorIdx];
    setCards([...cards, { id: "c"+Date.now(), name: cardForm.name, last4: cardForm.last4, color: col, limit: Number(cardForm.limit), paid: 0 }]);
    setCardForm(blankCard);
    setShowAddCard(false);
  };

  // Edit Card
  const saveEditCard = () => {
    if (!showEditCard.name || !showEditCard.last4 || !showEditCard.limit) { triggerShake(); return; }
    const col = CARD_COLORS[showEditCard.colorIdx ?? 0];
    setCards(cards.map(c => c.id === showEditCard.id ? { ...c, name: showEditCard.name, last4: showEditCard.last4, limit: Number(showEditCard.limit), color: col } : c));
    setShowEditCard(null);
  };

  // Delete Card
  const deleteCard = (id) => {
    setCards(cards.filter(c => c.id !== id));
    setTxs(txs.filter(t => t.cardId !== id));
    setPayments(payments.filter(p => p.cardId !== id));
    if (activeCard >= cards.length - 1) setActiveCard(0);
    setShowDeleteConfirm(null);
    setShowEditCard(null);
  };

  const grouped = txs.reduce((g, t) => { (g[t.date] = g[t.date] || []).push(t); return g; }, {});
  const currentCard = cards[activeCard] || { id: "", name: "", last4: "", color: ["#333","#555"], limit: 0, paid: 0 };
  const balance = getCardBalance(currentCard.id);
  const spend = getCardSpend(currentCard.id);
  const paid = getCardPaid(currentCard.id);
  const usedPct = Math.min((spend / currentCard.limit) * 100, 100);

  const inputStyle = {
    background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.15)",
    borderRadius: 14, padding: "13px 16px", color: "#fff", fontSize: 15,
    width: "100%", fontFamily: "inherit",
  };
  const labelStyle = { color: "#777", fontSize: 12, fontWeight: 600, marginBottom: 6, display: "block" };

  return (
    <div style={{
      display: "flex", justifyContent: "center", alignItems: "center",
      minHeight: "100vh", background: "linear-gradient(135deg,#1a1a2e,#16213e,#0f3460)",
      fontFamily: "'Sarabun','Noto Sans Thai',sans-serif",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sarabun:wght@300;400;600;700;800&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        ::-webkit-scrollbar{display:none}
        .slide{transition:transform .3s cubic-bezier(.34,1.56,.64,1)}
        .slide:active{transform:scale(.97)}
        .tx-row{animation:slideIn .25s ease}
        @keyframes slideIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        .fab{transition:all .2s cubic-bezier(.34,1.56,.64,1)}
        .fab:active{transform:scale(.88) rotate(45deg)}
        .shake{animation:shake .4s}
        @keyframes shake{0%,100%{transform:translateX(0)}25%{transform:translateX(-8px)}75%{transform:translateX(8px)}}
        .overlay{animation:fadeIn .2s ease}
        @keyframes fadeIn{from{opacity:0}to{opacity:1}}
        .sheet{animation:slideUp .3s cubic-bezier(.34,1.56,.64,1)}
        @keyframes slideUp{from{transform:translateY(100%)}to{transform:translateY(0)}}
        .ripple:active{opacity:.7}
        select{-webkit-appearance:none}
        input:focus,select:focus{outline:none;border-color:rgba(255,107,107,.6)!important}
        .del-btn:active{transform:scale(.9)}
        .edit-btn:active{transform:scale(.9)}
      `}</style>

      {/* iPhone Frame */}
      <div style={{
        width:393, height:852, background:"#080818", borderRadius:50, overflow:"hidden",
        position:"relative", boxShadow:"0 30px 80px rgba(0,0,0,.7),inset 0 0 0 2px #2a2a4a,0 0 0 8px #111",
      }}>
        {/* Status Bar */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 28px 0",color:"#fff",fontSize:12,fontWeight:600}}>
          <span>9:41</span>
          <div style={{width:120,height:34,background:"#080818",borderRadius:20,position:"absolute",left:"50%",transform:"translateX(-50%)",top:0}}/>
          <div style={{display:"flex",gap:6,alignItems:"center"}}><span>●●●</span><span>WiFi</span><span>🔋</span></div>
        </div>

        {/* Scrollable Content */}
        <div style={{height:"calc(100% - 82px)",overflowY:"auto",paddingBottom:20}}>

          {/* ═══════════════ HOME TAB ═══════════════ */}
          {tab === "home" && (
            <div>
              <div style={{padding:"16px 24px 0",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div>
                  <p style={{color:"#8888aa",fontSize:12}}>ยอดใช้จ่ายรวมทุกบัตร</p>
                  <p style={{color:"#fff",fontSize:34,fontWeight:800,letterSpacing:-1}}>
                    ฿{txs.reduce((s,t)=>s+t.amount,0).toLocaleString()}
                  </p>
                </div>
                <div style={{width:44,height:44,borderRadius:22,background:"linear-gradient(135deg,#FF6B6B,#FF9F43)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>👤</div>
              </div>

              {/* Empty state - no cards yet */}
              {cards.length === 0 && (
                <div style={{margin:"40px 24px",background:"rgba(255,255,255,.05)",borderRadius:24,padding:"40px 24px",textAlign:"center",border:"1px dashed rgba(255,255,255,.15)"}}>
                  <p style={{fontSize:52,marginBottom:16}}>💳</p>
                  <p style={{color:"#fff",fontSize:17,fontWeight:700,marginBottom:8}}>ยังไม่มีบัตรเครดิต</p>
                  <p style={{color:"#555",fontSize:13,marginBottom:24}}>เริ่มต้นด้วยการเพิ่มบัตรเครดิตของคุณ</p>
                  <button onClick={()=>setTab("cards")} style={{background:"linear-gradient(135deg,#FF6B6B,#FF9F43)",border:"none",borderRadius:14,padding:"12px 28px",color:"#fff",fontSize:14,fontWeight:700,cursor:"pointer"}}>➕ เพิ่มบัตรเครดิต</button>
                </div>
              )}

              {/* Card Carousel */}
              {cards.length > 0 && <div style={{padding:"20px 0 0",overflowX:"auto",display:"flex",gap:16,paddingLeft:24}}>
                {cards.map((card,i) => {
                  const sp = getCardSpend(card.id);
                  const bal = getCardBalance(card.id);
                  const pct = Math.min((sp/card.limit)*100,100);
                  return (
                    <div key={card.id} className="slide" onClick={()=>setActiveCard(i)} style={{
                      minWidth:300,height:170,borderRadius:24,
                      background:`linear-gradient(135deg,${card.color[0]},${card.color[1]})`,
                      padding:22,cursor:"pointer",flexShrink:0,
                      boxShadow: activeCard===i?`0 12px 40px ${card.color[0]}66`:"0 4px 20px rgba(0,0,0,.3)",
                      border: activeCard===i?"2px solid rgba(255,255,255,.4)":"2px solid transparent",
                      position:"relative",overflow:"hidden",
                    }}>
                      <div style={{position:"absolute",top:-20,right:-20,width:100,height:100,borderRadius:"50%",background:"rgba(255,255,255,.1)"}}/>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                        <div>
                          <p style={{color:"rgba(255,255,255,.8)",fontSize:11,fontWeight:600}}>{card.name}</p>
                          <p style={{color:"rgba(255,255,255,.6)",fontSize:12,marginTop:2}}>•••• {card.last4}</p>
                        </div>
                        <div style={{background:"rgba(255,255,255,.2)",borderRadius:8,padding:"3px 10px",fontSize:11,color:"#fff",fontWeight:700}}>VISA</div>
                      </div>
                      <div style={{marginTop:10,display:"flex",justifyContent:"space-between"}}>
                        <div>
                          <p style={{color:"rgba(255,255,255,.7)",fontSize:10}}>ค้างชำระ</p>
                          <p style={{color:"#fff",fontSize:20,fontWeight:800}}>฿{bal.toLocaleString()}</p>
                        </div>
                        <div style={{textAlign:"right"}}>
                          <p style={{color:"rgba(255,255,255,.7)",fontSize:10}}>วงเงิน</p>
                          <p style={{color:"#fff",fontSize:14,fontWeight:700}}>฿{card.limit.toLocaleString()}</p>
                        </div>
                      </div>
                      <div style={{marginTop:10,background:"rgba(255,255,255,.2)",borderRadius:4,height:4,overflow:"hidden"}}>
                        <div style={{height:"100%",borderRadius:4,width:`${pct}%`,background:"rgba(255,255,255,.9)",transition:"width .6s"}}/>
                      </div>
                      <p style={{color:"rgba(255,255,255,.7)",fontSize:10,marginTop:4}}>{pct.toFixed(0)}% ของวงเงิน</p>
                    </div>
                  );
                })}
                <div style={{minWidth:24,flexShrink:0}}/>
              </div>}

              {/* Action Buttons - only show when cards exist */}
              {cards.length > 0 && <div style={{display:"flex",gap:12,padding:"16px 24px 0"}}>
                <button onClick={()=>setShowPayment(true)} style={{
                  flex:1,background:"linear-gradient(135deg,#00CEC9,#00B894)",border:"none",borderRadius:16,
                  padding:"14px",color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",
                  boxShadow:"0 6px 20px rgba(0,206,201,.35)",
                }}>💰 ชำระบัตร</button>
                <button onClick={()=>setShowAddTx(true)} style={{
                  flex:1,background:"linear-gradient(135deg,#FF6B6B,#FF9F43)",border:"none",borderRadius:16,
                  padding:"14px",color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",
                  boxShadow:"0 6px 20px rgba(255,107,107,.35)",
                }}>➕ เพิ่มรายการ</button>
              </div>}

              {/* Transactions */}
              <div style={{padding:"20px 24px 0"}}>
                <p style={{color:"#fff",fontSize:16,fontWeight:700,marginBottom:14}}>รายการทั้งหมด</p>
                {txs.length === 0 && cards.length > 0 && (
                  <div style={{textAlign:"center",padding:"30px 0"}}>
                    <p style={{fontSize:36,marginBottom:10}}>📋</p>
                    <p style={{color:"#555",fontSize:14}}>ยังไม่มีรายการใช้จ่าย</p>
                    <p style={{color:"#444",fontSize:12,marginTop:4}}>กด "เพิ่มรายการ" เพื่อบันทึก</p>
                  </div>
                )}
                {Object.entries(grouped).map(([date,items])=>(
                  <div key={date}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:10}}>
                      <p style={{color:"#555",fontSize:11,fontWeight:600,letterSpacing:.5}}>{date.toUpperCase()}</p>
                      <p style={{color:"#555",fontSize:11}}>฿{items.reduce((s,t)=>s+t.amount,0).toLocaleString()}</p>
                    </div>
                    {items.map(tx=>{
                      const card = getCard(tx.cardId);
                      return (
                        <div key={tx.id} className="tx-row" style={{
                          display:"flex",alignItems:"center",marginBottom:10,
                          background:"rgba(255,255,255,.05)",borderRadius:16,padding:"12px 14px",
                          border:"1px solid rgba(255,255,255,.07)",
                        }}>
                          <div style={{
                            width:42,height:42,borderRadius:13,background:`${tx.color}22`,
                            display:"flex",alignItems:"center",justifyContent:"center",
                            fontSize:20,marginRight:12,border:`1px solid ${tx.color}44`,flexShrink:0,
                          }}>{tx.icon}</div>
                          <div style={{flex:1,minWidth:0}}>
                            <p style={{color:"#fff",fontSize:14,fontWeight:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{tx.desc}</p>
                            <p style={{color:"#555",fontSize:11,marginTop:2}}>{card?.name} •••• {card?.last4}</p>
                          </div>
                          <div style={{display:"flex",alignItems:"center",gap:8,marginLeft:8}}>
                            <p style={{color:tx.color,fontSize:15,fontWeight:800}}>-฿{tx.amount.toLocaleString()}</p>
                            <button className="edit-btn" onClick={()=>setShowEditTx({...tx,amount:String(tx.amount)})} style={{
                              background:"rgba(255,255,255,.08)",border:"none",borderRadius:8,
                              width:28,height:28,cursor:"pointer",fontSize:13,flexShrink:0,
                              display:"flex",alignItems:"center",justifyContent:"center",transition:"all .2s",
                            }}>✏️</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ═══════════════ BALANCE TAB ═══════════════ */}
          {tab === "balance" && (
            <div style={{padding:"20px 24px"}}>
              <p style={{color:"#fff",fontSize:22,fontWeight:800,marginBottom:6}}>สรุปยอดค้างชำระ</p>
              <p style={{color:"#666",fontSize:13,marginBottom:20}}>ยอดรวมทุกบัตร: <span style={{color:"#FF6B6B",fontWeight:700}}>฿{cards.reduce((s,c)=>s+getCardBalance(c.id),0).toLocaleString()}</span></p>

              {cards.map(card=>{
                const sp = getCardSpend(card.id);
                const pd = getCardPaid(card.id);
                const bal = sp - pd;
                const pct = Math.min((sp/card.limit)*100,100);
                const statusColor = bal <= 0 ? "#55EFC4" : bal > card.limit*0.8 ? "#FF6B6B" : "#FF9F43";
                return (
                  <div key={card.id} style={{
                    background:`linear-gradient(135deg,${card.color[0]}18,${card.color[1]}18)`,
                    borderRadius:20,padding:20,marginBottom:16,
                    border:`1px solid ${card.color[0]}44`,
                  }}>
                    {/* Card Header */}
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14}}>
                      <div>
                        <p style={{color:"#fff",fontSize:16,fontWeight:700}}>{card.name}</p>
                        <p style={{color:"#666",fontSize:12}}>•••• {card.last4} | วงเงิน ฿{card.limit.toLocaleString()}</p>
                      </div>
                      <div style={{
                        background: bal<=0?"rgba(85,239,196,.2)":"rgba(255,107,107,.15)",
                        borderRadius:10,padding:"4px 10px",
                      }}>
                        <p style={{color:statusColor,fontSize:11,fontWeight:700}}>{bal<=0?"✅ ชำระแล้ว":"⚠️ ค้างชำระ"}</p>
                      </div>
                    </div>

                    {/* 3 Numbers */}
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:14}}>
                      {[
                        {label:"ยอดใช้จ่าย",value:sp,color:card.color[0]},
                        {label:"ชำระแล้ว",value:pd,color:"#55EFC4"},
                        {label:"ค้างชำระ",value:bal,color:statusColor},
                      ].map((item,i)=>(
                        <div key={i} style={{background:"rgba(0,0,0,.25)",borderRadius:12,padding:"10px 12px",textAlign:"center"}}>
                          <p style={{color:item.color,fontSize:16,fontWeight:800}}>฿{item.value.toLocaleString()}</p>
                          <p style={{color:"#666",fontSize:10,marginTop:3}}>{item.label}</p>
                        </div>
                      ))}
                    </div>

                    {/* Usage Bar */}
                    <div style={{background:"rgba(0,0,0,.3)",borderRadius:6,height:8,overflow:"hidden",marginBottom:6}}>
                      <div style={{
                        height:"100%",borderRadius:6,
                        width:`${pct}%`,
                        background:`linear-gradient(90deg,${card.color[0]},${card.color[1]})`,
                        transition:"width .6s",
                      }}/>
                    </div>
                    <div style={{display:"flex",justifyContent:"space-between"}}>
                      <p style={{color:"#555",fontSize:10}}>ใช้ไป {pct.toFixed(0)}%</p>
                      <p style={{color:"#555",fontSize:10}}>เหลือ ฿{(card.limit-sp).toLocaleString()}</p>
                    </div>

                    {/* Payment History for this card */}
                    {payments.filter(p=>p.cardId===card.id).length>0 && (
                      <div style={{marginTop:14}}>
                        <p style={{color:"#888",fontSize:11,fontWeight:600,marginBottom:8}}>ประวัติการชำระ</p>
                        {payments.filter(p=>p.cardId===card.id).map(pay=>(
                          <div key={pay.id} style={{
                            display:"flex",alignItems:"center",
                            background:"rgba(85,239,196,.08)",borderRadius:12,
                            padding:"10px 12px",marginBottom:6,
                            border:"1px solid rgba(85,239,196,.15)",
                          }}>
                            <span style={{fontSize:18,marginRight:10}}>💰</span>
                            <div style={{flex:1}}>
                              <p style={{color:"#55EFC4",fontSize:13,fontWeight:700}}>+฿{pay.amount.toLocaleString()}</p>
                              <p style={{color:"#555",fontSize:11}}>{pay.date}{pay.note?` · ${pay.note}`:""}</p>
                            </div>
                            <button className="edit-btn" onClick={()=>setShowEditPayment({...pay,amount:String(pay.amount)})} style={{
                              background:"rgba(255,255,255,.08)",border:"none",borderRadius:8,
                              width:28,height:28,cursor:"pointer",fontSize:12,
                              display:"flex",alignItems:"center",justifyContent:"center",transition:"all .2s",
                            }}>✏️</button>
                          </div>
                        ))}
                      </div>
                    )}

                    <button onClick={()=>{setPayForm({cardId:card.id,amount:"",date:"27 พ.ค.",note:""});setShowPayment(true);}} style={{
                      width:"100%",marginTop:14,
                      background:`linear-gradient(135deg,${card.color[0]},${card.color[1]})`,
                      border:"none",borderRadius:14,padding:"12px",
                      color:"#fff",fontSize:14,fontWeight:700,cursor:"pointer",
                      boxShadow:`0 6px 20px ${card.color[0]}44`,
                    }}>💳 ชำระบัตรนี้</button>
                  </div>
                );
              })}
            </div>
          )}

          {/* ═══════════════ CARDS MANAGEMENT TAB ═══════════════ */}
          {tab === "cards" && (
            <div style={{padding:"20px 24px"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
                <div>
                  <p style={{color:"#fff",fontSize:22,fontWeight:800}}>จัดการบัตรเครดิต</p>
                  <p style={{color:"#666",fontSize:12,marginTop:2}}>{cards.length} บัตร</p>
                </div>
                <button onClick={()=>{setCardForm(blankCard);setShowAddCard(true);}} style={{
                  background:"linear-gradient(135deg,#FF6B6B,#FF9F43)",border:"none",
                  borderRadius:14,padding:"10px 16px",color:"#fff",fontSize:13,fontWeight:700,
                  cursor:"pointer",boxShadow:"0 6px 20px rgba(255,107,107,.4)",
                }}>＋ เพิ่มบัตร</button>
              </div>

              {cards.map((card,i) => {
                const sp = getCardSpend(card.id);
                const bal = getCardBalance(card.id);
                const pct = Math.min((sp/card.limit)*100,100);
                return (
                  <div key={card.id} style={{
                    borderRadius:22,marginBottom:16,overflow:"hidden",
                    border:`1px solid ${card.color[0]}44`,
                  }}>
                    {/* Card Visual */}
                    <div style={{
                      height:130,
                      background:`linear-gradient(135deg,${card.color[0]},${card.color[1]})`,
                      padding:"18px 20px",position:"relative",overflow:"hidden",
                    }}>
                      <div style={{position:"absolute",top:-15,right:-15,width:90,height:90,borderRadius:"50%",background:"rgba(255,255,255,.12)"}}/>
                      <div style={{position:"absolute",bottom:-25,left:10,width:110,height:110,borderRadius:"50%",background:"rgba(255,255,255,.07)"}}/>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                        <div>
                          <p style={{color:"rgba(255,255,255,.85)",fontSize:13,fontWeight:700}}>{card.name}</p>
                          <p style={{color:"rgba(255,255,255,.6)",fontSize:12,marginTop:2,letterSpacing:1}}>•••• •••• •••• {card.last4}</p>
                        </div>
                        <button onClick={()=>setShowEditCard({...card,limit:String(card.limit),colorIdx:CARD_COLORS.findIndex(c=>c[0]===card.color[0])??0})} style={{
                          background:"rgba(255,255,255,.25)",border:"none",borderRadius:10,
                          padding:"6px 12px",color:"#fff",fontSize:12,fontWeight:700,cursor:"pointer",
                        }}>✏️ แก้ไข</button>
                      </div>
                      <div style={{marginTop:12,display:"flex",justifyContent:"space-between",alignItems:"flex-end"}}>
                        <div>
                          <p style={{color:"rgba(255,255,255,.7)",fontSize:10}}>ค้างชำระ</p>
                          <p style={{color:"#fff",fontSize:18,fontWeight:800}}>฿{bal.toLocaleString()}</p>
                        </div>
                        <div style={{textAlign:"right"}}>
                          <p style={{color:"rgba(255,255,255,.7)",fontSize:10}}>วงเงิน</p>
                          <p style={{color:"#fff",fontSize:14,fontWeight:700}}>฿{card.limit.toLocaleString()}</p>
                        </div>
                      </div>
                    </div>

                    {/* Card Stats */}
                    <div style={{background:"rgba(255,255,255,.04)",padding:"14px 20px"}}>
                      <div style={{background:"rgba(0,0,0,.3)",borderRadius:6,height:6,overflow:"hidden",marginBottom:8}}>
                        <div style={{height:"100%",borderRadius:6,width:`${pct}%`,background:`linear-gradient(90deg,${card.color[0]},${card.color[1]})`,transition:"width .6s"}}/>
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:12}}>
                        <p style={{color:"#555",fontSize:11}}>ใช้ไป {pct.toFixed(0)}%</p>
                        <p style={{color:"#555",fontSize:11}}>คงเหลือ ฿{(card.limit-sp).toLocaleString()}</p>
                      </div>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:12}}>
                        {[
                          {label:"รายการ",value:`${txs.filter(t=>t.cardId===card.id).length} รายการ`,color:"#aaa"},
                          {label:"ใช้จ่าย",value:`฿${sp.toLocaleString()}`,color:card.color[0]},
                          {label:"ชำระแล้ว",value:`฿${getCardPaid(card.id).toLocaleString()}`,color:"#55EFC4"},
                        ].map((s,i)=>(
                          <div key={i} style={{background:"rgba(0,0,0,.25)",borderRadius:10,padding:"8px 10px",textAlign:"center"}}>
                            <p style={{color:s.color,fontSize:12,fontWeight:700}}>{s.value}</p>
                            <p style={{color:"#555",fontSize:10,marginTop:2}}>{s.label}</p>
                          </div>
                        ))}
                      </div>
                      <button onClick={()=>setShowDeleteConfirm({type:"card",id:card.id})} style={{
                        width:"100%",background:"rgba(255,107,107,.1)",border:"1px solid rgba(255,107,107,.25)",
                        borderRadius:12,padding:"10px",color:"#FF6B6B",fontSize:13,fontWeight:700,cursor:"pointer",
                      }}>🗑 ลบบัตรนี้</button>
                    </div>
                  </div>
                );
              })}

              {cards.length === 0 && (
                <div style={{textAlign:"center",padding:"60px 0"}}>
                  <p style={{fontSize:48,marginBottom:16}}>💳</p>
                  <p style={{color:"#555",fontSize:15}}>ยังไม่มีบัตรเครดิต</p>
                  <p style={{color:"#444",fontSize:13,marginTop:6}}>กด "เพิ่มบัตร" เพื่อเริ่มต้น</p>
                </div>
              )}
            </div>
          )}

        </div>

        {/* Bottom Nav */}
        <div style={{
          position:"absolute",bottom:0,left:0,right:0,height:82,
          background:"rgba(8,8,24,.96)",backdropFilter:"blur(20px)",
          borderTop:"1px solid rgba(255,255,255,.08)",
          display:"flex",justifyContent:"space-around",alignItems:"center",padding:"0 0 16px",
        }}>
          {[
            {id:"home",icon:"🏠",label:"หน้าหลัก"},
            {id:"balance",icon:"💳",label:"ยอดค้าง"},
            {id:"cards",icon:"➕",label:"จัดการบัตร"},
          ].map(t=>(
            <button key={t.id} onClick={()=>setTab(t.id)} style={{
              background:"none",border:"none",cursor:"pointer",
              display:"flex",flexDirection:"column",alignItems:"center",gap:4,padding:"8px 20px",borderRadius:16,
            }}>
              <span style={{fontSize:22,filter:tab===t.id?"none":"grayscale(1) opacity(.4)"}}>{t.icon}</span>
              <span style={{fontSize:11,fontWeight:600,color:tab===t.id?"#FF6B6B":"#444"}}>{t.label}</span>
              {tab===t.id && <div style={{width:4,height:4,borderRadius:2,background:"#FF6B6B",marginTop:-2}}/>}
            </button>
          ))}
        </div>

        {/* ═══ MODAL: ADD TRANSACTION ═══ */}
        {showAddTx && (
          <div className="overlay" onClick={e=>e.target===e.currentTarget&&setShowAddTx(false)} style={{position:"absolute",inset:0,background:"rgba(0,0,0,.7)",display:"flex",alignItems:"flex-end",zIndex:30}}>
            <div className="sheet" style={{width:"100%",background:"#0e0e24",borderRadius:"28px 28px 0 0",padding:"24px 24px 40px",border:"1px solid rgba(255,255,255,.1)"}}>
              <div style={{width:40,height:4,background:"#333",borderRadius:2,margin:"0 auto 20px"}}/>
              <p style={{color:"#fff",fontSize:18,fontWeight:800,marginBottom:18}}>➕ เพิ่มรายการใช้จ่าย</p>
              <div className={shake?"shake":""} style={{display:"flex",flexDirection:"column",gap:14}}>
                <div>
                  <label style={labelStyle}>รายละเอียด</label>
                  <input value={txForm.desc} onChange={e=>setTxForm({...txForm,desc:e.target.value})} placeholder="เช่น ข้าวผัด, Grab, Shopee" style={inputStyle}/>
                </div>
                <div>
                  <label style={labelStyle}>จำนวนเงิน (บาท)</label>
                  <input value={txForm.amount} onChange={e=>setTxForm({...txForm,amount:e.target.value})} type="number" placeholder="0.00" style={inputStyle}/>
                </div>
                <div>
                  <label style={labelStyle}>หมวดหมู่</label>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                    {CATEGORIES.map(c=>(
                      <button key={c.name} onClick={()=>setTxForm({...txForm,cat:c.name})} style={{
                        padding:"8px 12px",borderRadius:10,border:"none",cursor:"pointer",fontSize:12,fontWeight:600,
                        background:txForm.cat===c.name?c.color:"rgba(255,255,255,.08)",
                        color:txForm.cat===c.name?"#fff":"#888",transition:"all .2s",
                      }}>{c.icon} {c.name}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>บัตรเครดิต</label>
                  <select value={txForm.cardId} onChange={e=>setTxForm({...txForm,cardId:e.target.value})} style={inputStyle}>
                    {cards.map(c=><option key={c.id} value={c.id} style={{background:"#1a1a3a"}}>{c.name} (•••• {c.last4})</option>)}
                  </select>
                </div>
                <button onClick={addTx} style={{background:"linear-gradient(135deg,#FF6B6B,#FF9F43)",border:"none",borderRadius:16,padding:"15px",color:"#fff",fontSize:15,fontWeight:700,cursor:"pointer",boxShadow:"0 8px 24px rgba(255,107,107,.4)"}}>✓ บันทึกรายการ</button>
              </div>
            </div>
          </div>
        )}

        {/* ═══ MODAL: EDIT TRANSACTION ═══ */}
        {showEditTx && (
          <div className="overlay" onClick={e=>e.target===e.currentTarget&&setShowEditTx(null)} style={{position:"absolute",inset:0,background:"rgba(0,0,0,.75)",display:"flex",alignItems:"flex-end",zIndex:30}}>
            <div className="sheet" style={{width:"100%",background:"#0e0e24",borderRadius:"28px 28px 0 0",padding:"24px 24px 40px",border:"1px solid rgba(255,255,255,.1)"}}>
              <div style={{width:40,height:4,background:"#333",borderRadius:2,margin:"0 auto 20px"}}/>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
                <p style={{color:"#fff",fontSize:18,fontWeight:800}}>✏️ แก้ไขรายการ</p>
                <button onClick={()=>setShowDeleteConfirm({type:"tx",id:showEditTx.id})} style={{
                  background:"rgba(255,107,107,.15)",border:"1px solid rgba(255,107,107,.3)",
                  borderRadius:10,padding:"6px 12px",color:"#FF6B6B",fontSize:12,fontWeight:700,cursor:"pointer",
                }}>🗑 ลบ</button>
              </div>
              <div className={shake?"shake":""} style={{display:"flex",flexDirection:"column",gap:14}}>
                <div>
                  <label style={labelStyle}>รายละเอียด</label>
                  <input value={showEditTx.desc} onChange={e=>setShowEditTx({...showEditTx,desc:e.target.value})} style={inputStyle}/>
                </div>
                <div>
                  <label style={labelStyle}>จำนวนเงิน (บาท)</label>
                  <input value={showEditTx.amount} onChange={e=>setShowEditTx({...showEditTx,amount:e.target.value})} type="number" style={inputStyle}/>
                </div>
                <div>
                  <label style={labelStyle}>หมวดหมู่</label>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                    {CATEGORIES.map(c=>(
                      <button key={c.name} onClick={()=>setShowEditTx({...showEditTx,cat:c.name})} style={{
                        padding:"8px 12px",borderRadius:10,border:"none",cursor:"pointer",fontSize:12,fontWeight:600,
                        background:showEditTx.cat===c.name?c.color:"rgba(255,255,255,.08)",
                        color:showEditTx.cat===c.name?"#fff":"#888",transition:"all .2s",
                      }}>{c.icon} {c.name}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>บัตรเครดิต</label>
                  <select value={showEditTx.cardId} onChange={e=>setShowEditTx({...showEditTx,cardId:e.target.value})} style={inputStyle}>
                    {cards.map(c=><option key={c.id} value={c.id} style={{background:"#1a1a3a"}}>{c.name} (•••• {c.last4})</option>)}
                  </select>
                </div>
                <div style={{display:"flex",gap:10}}>
                  <button onClick={()=>setShowEditTx(null)} style={{flex:1,background:"rgba(255,255,255,.08)",border:"none",borderRadius:16,padding:"14px",color:"#aaa",fontSize:14,fontWeight:700,cursor:"pointer"}}>ยกเลิก</button>
                  <button onClick={saveEditTx} style={{flex:2,background:"linear-gradient(135deg,#A29BFE,#6C5CE7)",border:"none",borderRadius:16,padding:"14px",color:"#fff",fontSize:14,fontWeight:700,cursor:"pointer",boxShadow:"0 6px 20px rgba(162,155,254,.4)"}}>✓ บันทึกการแก้ไข</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ═══ MODAL: ADD PAYMENT ═══ */}
        {showPayment && (
          <div className="overlay" onClick={e=>e.target===e.currentTarget&&setShowPayment(false)} style={{position:"absolute",inset:0,background:"rgba(0,0,0,.7)",display:"flex",alignItems:"flex-end",zIndex:30}}>
            <div className="sheet" style={{width:"100%",background:"#0e0e24",borderRadius:"28px 28px 0 0",padding:"24px 24px 40px",border:"1px solid rgba(255,255,255,.1)"}}>
              <div style={{width:40,height:4,background:"#333",borderRadius:2,margin:"0 auto 20px"}}/>
              <p style={{color:"#fff",fontSize:18,fontWeight:800,marginBottom:18}}>💰 บันทึกการชำระบัตร</p>
              <div className={shake?"shake":""} style={{display:"flex",flexDirection:"column",gap:14}}>
                <div>
                  <label style={labelStyle}>เลือกบัตรที่ชำระ</label>
                  <select value={payForm.cardId} onChange={e=>setPayForm({...payForm,cardId:e.target.value})} style={inputStyle}>
                    {cards.map(c=><option key={c.id} value={c.id} style={{background:"#1a1a3a"}}>{c.name} — ค้าง ฿{getCardBalance(c.id).toLocaleString()}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>จำนวนที่ชำระ (บาท)</label>
                  <div style={{position:"relative"}}>
                    <input value={payForm.amount} onChange={e=>setPayForm({...payForm,amount:e.target.value})} type="number" placeholder="0.00" style={inputStyle}/>
                    <button onClick={()=>setPayForm({...payForm,amount:String(getCardBalance(payForm.cardId))})} style={{
                      position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",
                      background:"rgba(85,239,196,.2)",border:"none",borderRadius:8,
                      padding:"4px 10px",color:"#55EFC4",fontSize:11,fontWeight:700,cursor:"pointer",
                    }}>ชำระทั้งหมด</button>
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>วันที่ชำระ</label>
                  <input value={payForm.date} onChange={e=>setPayForm({...payForm,date:e.target.value})} placeholder="เช่น 27 พ.ค." style={inputStyle}/>
                </div>
                <div>
                  <label style={labelStyle}>หมายเหตุ (ไม่บังคับ)</label>
                  <input value={payForm.note} onChange={e=>setPayForm({...payForm,note:e.target.value})} placeholder="เช่น โอนผ่าน App, จ่ายขั้นต่ำ" style={inputStyle}/>
                </div>
                <div style={{background:"rgba(85,239,196,.08)",borderRadius:14,padding:"12px 14px",border:"1px solid rgba(85,239,196,.2)"}}>
                  <p style={{color:"#888",fontSize:12}}>ยอดค้างปัจจุบัน: <span style={{color:"#FF6B6B",fontWeight:700}}>฿{getCardBalance(payForm.cardId).toLocaleString()}</span></p>
                  {payForm.amount && <p style={{color:"#888",fontSize:12,marginTop:4}}>หลังชำระ: <span style={{color:"#55EFC4",fontWeight:700}}>฿{Math.max(0,getCardBalance(payForm.cardId)-Number(payForm.amount)).toLocaleString()}</span></p>}
                </div>
                <button onClick={addPayment} style={{background:"linear-gradient(135deg,#00CEC9,#00B894)",border:"none",borderRadius:16,padding:"15px",color:"#fff",fontSize:15,fontWeight:700,cursor:"pointer",boxShadow:"0 8px 24px rgba(0,206,201,.4)"}}>✓ บันทึกการชำระ</button>
              </div>
            </div>
          </div>
        )}

        {/* ═══ MODAL: EDIT PAYMENT ═══ */}
        {showEditPayment && (
          <div className="overlay" onClick={e=>e.target===e.currentTarget&&setShowEditPayment(null)} style={{position:"absolute",inset:0,background:"rgba(0,0,0,.75)",display:"flex",alignItems:"flex-end",zIndex:30}}>
            <div className="sheet" style={{width:"100%",background:"#0e0e24",borderRadius:"28px 28px 0 0",padding:"24px 24px 40px",border:"1px solid rgba(255,255,255,.1)"}}>
              <div style={{width:40,height:4,background:"#333",borderRadius:2,margin:"0 auto 20px"}}/>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
                <p style={{color:"#fff",fontSize:18,fontWeight:800}}>✏️ แก้ไขการชำระ</p>
                <button onClick={()=>setShowDeleteConfirm({type:"payment",id:showEditPayment.id})} style={{
                  background:"rgba(255,107,107,.15)",border:"1px solid rgba(255,107,107,.3)",
                  borderRadius:10,padding:"6px 12px",color:"#FF6B6B",fontSize:12,fontWeight:700,cursor:"pointer",
                }}>🗑 ลบ</button>
              </div>
              <div className={shake?"shake":""} style={{display:"flex",flexDirection:"column",gap:14}}>
                <div>
                  <label style={labelStyle}>จำนวนที่ชำระ (บาท)</label>
                  <input value={showEditPayment.amount} onChange={e=>setShowEditPayment({...showEditPayment,amount:e.target.value})} type="number" style={inputStyle}/>
                </div>
                <div>
                  <label style={labelStyle}>วันที่ชำระ</label>
                  <input value={showEditPayment.date} onChange={e=>setShowEditPayment({...showEditPayment,date:e.target.value})} style={inputStyle}/>
                </div>
                <div>
                  <label style={labelStyle}>หมายเหตุ</label>
                  <input value={showEditPayment.note} onChange={e=>setShowEditPayment({...showEditPayment,note:e.target.value})} style={inputStyle}/>
                </div>
                <div style={{display:"flex",gap:10}}>
                  <button onClick={()=>setShowEditPayment(null)} style={{flex:1,background:"rgba(255,255,255,.08)",border:"none",borderRadius:16,padding:"14px",color:"#aaa",fontSize:14,fontWeight:700,cursor:"pointer"}}>ยกเลิก</button>
                  <button onClick={saveEditPayment} style={{flex:2,background:"linear-gradient(135deg,#00CEC9,#00B894)",border:"none",borderRadius:16,padding:"14px",color:"#fff",fontSize:14,fontWeight:700,cursor:"pointer",boxShadow:"0 6px 20px rgba(0,206,201,.4)"}}>✓ บันทึก</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ═══ MODAL: ADD CARD ═══ */}
        {showAddCard && (
          <div className="overlay" onClick={e=>e.target===e.currentTarget&&setShowAddCard(false)} style={{position:"absolute",inset:0,background:"rgba(0,0,0,.75)",display:"flex",alignItems:"flex-end",zIndex:30}}>
            <div className="sheet" style={{width:"100%",background:"#0e0e24",borderRadius:"28px 28px 0 0",padding:"24px 24px 40px",border:"1px solid rgba(255,255,255,.1)"}}>
              <div style={{width:40,height:4,background:"#333",borderRadius:2,margin:"0 auto 20px"}}/>
              <p style={{color:"#fff",fontSize:18,fontWeight:800,marginBottom:18}}>💳 เพิ่มบัตรเครดิตใหม่</p>
              <div className={shake?"shake":""} style={{display:"flex",flexDirection:"column",gap:14}}>
                <div>
                  <label style={labelStyle}>ชื่อบัตร (เช่น KBank Visa)</label>
                  <input value={cardForm.name} onChange={e=>setCardForm({...cardForm,name:e.target.value})} placeholder="ธนาคาร + ประเภทบัตร" style={inputStyle}/>
                </div>
                <div>
                  <label style={labelStyle}>เลข 4 หลักท้าย</label>
                  <input value={cardForm.last4} onChange={e=>setCardForm({...cardForm,last4:e.target.value.slice(0,4)})} placeholder="xxxx" maxLength={4} style={inputStyle}/>
                </div>
                <div>
                  <label style={labelStyle}>วงเงิน (บาท)</label>
                  <input value={cardForm.limit} onChange={e=>setCardForm({...cardForm,limit:e.target.value})} type="number" placeholder="เช่น 50000" style={inputStyle}/>
                </div>
                <div>
                  <label style={labelStyle}>สีบัตร</label>
                  <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                    {CARD_COLORS.map((col,i)=>(
                      <button key={i} onClick={()=>setCardForm({...cardForm,colorIdx:i})} style={{
                        width:44,height:44,borderRadius:12,border:"none",cursor:"pointer",
                        background:`linear-gradient(135deg,${col[0]},${col[1]})`,
                        boxShadow:cardForm.colorIdx===i?`0 0 0 3px #fff,0 0 0 5px ${col[0]}`:"none",
                        transition:"all .2s",
                      }}/>
                    ))}
                  </div>
                </div>
                {/* Preview */}
                <div style={{
                  height:80,borderRadius:16,padding:"14px 18px",position:"relative",overflow:"hidden",
                  background:`linear-gradient(135deg,${CARD_COLORS[cardForm.colorIdx][0]},${CARD_COLORS[cardForm.colorIdx][1]})`,
                }}>
                  <div style={{position:"absolute",top:-10,right:-10,width:70,height:70,borderRadius:"50%",background:"rgba(255,255,255,.12)"}}/>
                  <p style={{color:"rgba(255,255,255,.85)",fontSize:13,fontWeight:700}}>{cardForm.name||"ชื่อบัตร"}</p>
                  <p style={{color:"rgba(255,255,255,.6)",fontSize:12,marginTop:4}}>•••• •••• •••• {cardForm.last4||"xxxx"} | ฿{Number(cardForm.limit||0).toLocaleString()}</p>
                </div>
                <div style={{display:"flex",gap:10}}>
                  <button onClick={()=>setShowAddCard(false)} style={{flex:1,background:"rgba(255,255,255,.08)",border:"none",borderRadius:16,padding:"14px",color:"#aaa",fontSize:14,fontWeight:700,cursor:"pointer"}}>ยกเลิก</button>
                  <button onClick={addCard} style={{flex:2,background:"linear-gradient(135deg,#FF6B6B,#FF9F43)",border:"none",borderRadius:16,padding:"14px",color:"#fff",fontSize:14,fontWeight:700,cursor:"pointer",boxShadow:"0 6px 20px rgba(255,107,107,.4)"}}>✓ เพิ่มบัตร</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ═══ MODAL: EDIT CARD ═══ */}
        {showEditCard && (
          <div className="overlay" onClick={e=>e.target===e.currentTarget&&setShowEditCard(null)} style={{position:"absolute",inset:0,background:"rgba(0,0,0,.75)",display:"flex",alignItems:"flex-end",zIndex:30}}>
            <div className="sheet" style={{width:"100%",background:"#0e0e24",borderRadius:"28px 28px 0 0",padding:"24px 24px 40px",border:"1px solid rgba(255,255,255,.1)"}}>
              <div style={{width:40,height:4,background:"#333",borderRadius:2,margin:"0 auto 20px"}}/>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
                <p style={{color:"#fff",fontSize:18,fontWeight:800}}>✏️ แก้ไขข้อมูลบัตร</p>
                <button onClick={()=>setShowDeleteConfirm({type:"card",id:showEditCard.id})} style={{
                  background:"rgba(255,107,107,.15)",border:"1px solid rgba(255,107,107,.3)",
                  borderRadius:10,padding:"6px 12px",color:"#FF6B6B",fontSize:12,fontWeight:700,cursor:"pointer",
                }}>🗑 ลบ</button>
              </div>
              <div className={shake?"shake":""} style={{display:"flex",flexDirection:"column",gap:14}}>
                <div>
                  <label style={labelStyle}>ชื่อบัตร</label>
                  <input value={showEditCard.name} onChange={e=>setShowEditCard({...showEditCard,name:e.target.value})} style={inputStyle}/>
                </div>
                <div>
                  <label style={labelStyle}>เลข 4 หลักท้าย</label>
                  <input value={showEditCard.last4} onChange={e=>setShowEditCard({...showEditCard,last4:e.target.value.slice(0,4)})} maxLength={4} style={inputStyle}/>
                </div>
                <div>
                  <label style={labelStyle}>วงเงิน (บาท)</label>
                  <input value={showEditCard.limit} onChange={e=>setShowEditCard({...showEditCard,limit:e.target.value})} type="number" style={inputStyle}/>
                </div>
                <div>
                  <label style={labelStyle}>สีบัตร</label>
                  <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                    {CARD_COLORS.map((col,i)=>(
                      <button key={i} onClick={()=>setShowEditCard({...showEditCard,colorIdx:i})} style={{
                        width:44,height:44,borderRadius:12,border:"none",cursor:"pointer",
                        background:`linear-gradient(135deg,${col[0]},${col[1]})`,
                        boxShadow:showEditCard.colorIdx===i?`0 0 0 3px #fff,0 0 0 5px ${col[0]}`:"none",
                        transition:"all .2s",
                      }}/>
                    ))}
                  </div>
                </div>
                {/* Preview */}
                <div style={{
                  height:80,borderRadius:16,padding:"14px 18px",position:"relative",overflow:"hidden",
                  background:`linear-gradient(135deg,${CARD_COLORS[showEditCard.colorIdx??0][0]},${CARD_COLORS[showEditCard.colorIdx??0][1]})`,
                }}>
                  <div style={{position:"absolute",top:-10,right:-10,width:70,height:70,borderRadius:"50%",background:"rgba(255,255,255,.12)"}}/>
                  <p style={{color:"rgba(255,255,255,.85)",fontSize:13,fontWeight:700}}>{showEditCard.name||"ชื่อบัตร"}</p>
                  <p style={{color:"rgba(255,255,255,.6)",fontSize:12,marginTop:4}}>•••• •••• •••• {showEditCard.last4} | ฿{Number(showEditCard.limit||0).toLocaleString()}</p>
                </div>
                <div style={{display:"flex",gap:10}}>
                  <button onClick={()=>setShowEditCard(null)} style={{flex:1,background:"rgba(255,255,255,.08)",border:"none",borderRadius:16,padding:"14px",color:"#aaa",fontSize:14,fontWeight:700,cursor:"pointer"}}>ยกเลิก</button>
                  <button onClick={saveEditCard} style={{flex:2,background:"linear-gradient(135deg,#A29BFE,#6C5CE7)",border:"none",borderRadius:16,padding:"14px",color:"#fff",fontSize:14,fontWeight:700,cursor:"pointer",boxShadow:"0 6px 20px rgba(162,155,254,.4)"}}>✓ บันทึกการแก้ไข</button>
                </div>
              </div>
            </div>
          </div>
        )}


        {showDeleteConfirm && (
          <div className="overlay" style={{position:"absolute",inset:0,background:"rgba(0,0,0,.8)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:40,padding:24}}>
            <div className="sheet" style={{width:"100%",background:"#0e0e24",borderRadius:24,padding:28,border:"1px solid rgba(255,107,107,.3)"}}>
              <p style={{fontSize:40,textAlign:"center",marginBottom:12}}>🗑️</p>
              <p style={{color:"#fff",fontSize:17,fontWeight:700,textAlign:"center",marginBottom:8}}>ยืนยันการลบ?</p>
              <p style={{color:"#666",fontSize:13,textAlign:"center",marginBottom:24}}>{showDeleteConfirm?.type==="card"?"บัตรและรายการทั้งหมดในบัตรนี้จะถูกลบถาวร":"ข้อมูลนี้จะถูกลบถาวร ไม่สามารถกู้คืนได้"}</p>
              <div style={{display:"flex",gap:10}}>
                <button onClick={()=>setShowDeleteConfirm(null)} style={{flex:1,background:"rgba(255,255,255,.08)",border:"none",borderRadius:14,padding:"14px",color:"#aaa",fontSize:14,fontWeight:700,cursor:"pointer"}}>ยกเลิก</button>
                <button onClick={()=>showDeleteConfirm.type==="tx"?deleteTx(showDeleteConfirm.id):showDeleteConfirm.type==="payment"?deletePayment(showDeleteConfirm.id):deleteCard(showDeleteConfirm.id)} style={{flex:1,background:"linear-gradient(135deg,#FF6B6B,#e84393)",border:"none",borderRadius:14,padding:"14px",color:"#fff",fontSize:14,fontWeight:700,cursor:"pointer",boxShadow:"0 6px 20px rgba(255,107,107,.4)"}}>ลบเลย</button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

